/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from "react";
import Link from "helpers/link";
import { ReactComponent as Arrow } from "assets/svgs/accordion-arrow.svg";
import { ReactComponent as CartIcon } from "assets/svgs/atc-icon.svg";
import QuantitySelector from "components/quantity-selector";
import Stars from "components/stars";
import useProduct from "hooks/useProduct";
import { VariantSelectorCircles } from "components/variant-selector";
import { mapItems, trackQuantityChange } from "analytics";
import Money from "components/money";
import { CURRENCY } from "utils/constants";
import getProductAttributes from "helpers/get-product-attributes";
import NProgress from "nprogress";
import { widgets, badgesByVehicles } from "./data";

const presetVariantId = undefined;

const ProductItem = ({ data, handleAddToCart, variantColors, handleSelectItem }) => {
    const [quantity, setQuantity] = useState(1);
    const { product, setVariant, getVariantByOptions } = useProduct(data, presetVariantId);

    const reviewStars = product?.metafields?.filter((metafield) => metafield.key === "review_stars")[0];

    const isBimini = product?.title.toLowerCase().includes("bimini top");

    let hasOneDropdownWithOneValue;
    if (product.productType === "Accessories") {
        hasOneDropdownWithOneValue = (product?.options?.length > 0 && !product.options.every((option) => option.values.length === 1));
    } else {
        hasOneDropdownWithOneValue = (product?.options?.length > 0 && !product.options.every((option) => option.values.length === 0));
    }

    const checkoutAttributes = product?.checkout_attributes 
        ? Object.entries(product?.checkout_attributes)
            .reduce((acc, [key, value]) => (value ? ({ ...acc, [key]: value }) : acc), {})
        : product.tags;

    const checkoutCustomAttributes = product?.checkout_attributes 
        ? Object.entries(checkoutAttributes).reduce((acc, attr) => [...acc, { key: attr[0], value: attr[1].toString() }], []) : [];

    if (product?.item_metrics) {
        const itemMetrics = product.item_metrics;
        // eslint-disable-next-line no-loops/no-loops
        for (let i = 1; i <= 5; i += 1) {
            const num = i === 1 ? "" : i;
            if (itemMetrics[`item_category${num}`]) {
                checkoutCustomAttributes.push({
                    key: `_item_category${num}`,
                    value: `${itemMetrics[`item_category${num}`]}`,
                });
            }
        }
    }

    // ToDo: We are just using mapItems to get the item_variant for adding it to the customAttributes, we should refactor this
    const productAnalytics = mapItems([product]);
    if (productAnalytics?.[0]?.item_variant) {
        checkoutCustomAttributes.push({
            key: "_item_variant",
            value: productAnalytics[0].item_variant,
        });
    }
    // ----------------------------
    
    const handleQuantityChange = (value) => {
        // This sends add_to_cart event, even though its not added to cart
        // trackQuantityChange({
        //     ...product, variant: product.variant, quantity: value,
        // }, value);
        setQuantity(value);
    };  

    const widgetImages = () => {
        // Check if the product is a Bimini and if it's a Bow/Canvas/Boot type
        
        if (product?.title?.includes("Bimini")) {
            if ((
                product?.productType?.includes("Bow") 
                    || product?.productType?.includes("Canvas") 
                    || product?.productType?.includes("Boot")
            )) {
                return widgets.find((widget) => widget?.keys
                    ?.some((key) => product?.productType?.toLowerCase()?.includes(widget?.tag?.toLowerCase()) && product?.title?.toLowerCase()?.includes(key?.toLowerCase())));
            } 
        }

        return widgets.find((widget) => widget?.keys?.some((key) => product?.title?.toLowerCase()?.includes(key?.toLowerCase())));
    };

    const WarrantyBadge = () => {
        const findVehicleBadges = badgesByVehicles.find((badge) => product?.title?.toLowerCase().includes(badge.type));
        if (!findVehicleBadges) return null;
    
        const findVehicleWarranty = findVehicleBadges.badges.find((vehicleBadge) => product.title.toLowerCase().includes(vehicleBadge.key));
        if (!findVehicleWarranty) return null;
    
        return findVehicleWarranty.Component;
    };

    const truncateDesc = (() => {
        if (!data?.description) return "";

        const removeHtmlTags = (text) => text.replace(/<\/?[^>]+>/g, "");
        const getFirstWords = (text, numWords) => {
            const words = text.split(/\s+/);
            return words.slice(0, numWords).join(" ");
        };
        const rawText = removeHtmlTags(data.description);
        const truncatedText = getFirstWords(rawText, 70);

        return truncatedText.trim() + (rawText.length > truncatedText.length ? "..." : ""); 
    })();

    useEffect(() => {
        NProgress.done();
    }, []);

    return (
        <div
            key={data?._key}
            className="relative p-4 w-full bg-white border border-gray-300 mt-7 lg:p-2.5 h-full"
        >
            <div className="flex flex-wrap -mx-3 lg:flex-nowrap h-full">
                {WarrantyBadge() && (
                    <div className="!absolute w-40 -top-9 lg:-top-[2.15rem] -left-4 md:-left-8 z-10">
                        <WarrantyBadge />
                    </div>  
                )}
                <Link to={`/products/${product?.handle}`} onClick={() => { handleSelectItem(); NProgress.start(); }} className="flex justify-end">
                    <div className={`${isBimini ? "mt-auto" : "my-auto"} px-3 w-fit`}>
                        <figure
                            className="block text-center leading-none pt-2.5 md:pt-0 lg:max-h-[327px] lg:max-w-[260px]"
                        >
                            <img src={product?.currentVariant?.node?.image?.url ?? product?.image?.url} width={product?.image?.width} alt={getProductAttributes(product)} />
                        </figure>
                    </div>
                </Link>
                <div className="product-info lg:basis-1/2 lg:self-start xl:basis-[55%]">
                    <h3 className="text-base uppercase leading-8 md:text-lg lg:text-2xl">
                        <Link
                            className="text-left"
                            to={`/products/${product?.handle}`}
                            onClick={() => { handleSelectItem(); NProgress.start(); }}
                        >
                            {data.title}
                        </Link>
                    </h3>
                    <p className="text-small font-medium uppercase md:text-xs md:leading-5 lg:leading-7">
                        {getProductAttributes(product)}
                    </p>
                    <div className="pt-1.5 pb-1 w-full lg:pt-0.5">
                        <div className="flex text-xs leading-none">
                            <Stars rating={reviewStars?.value || 0} className="!gap-0.5" starColor="fill-orange-701" />
                        </div>
                    </div>
                    <div className="text-[0.95rem] w-full text-[#000000b3] leading-5 mb-5 line-clamp-3 !description">
                        <p dangerouslySetInnerHTML={{ __html: truncateDesc }} />
                    </div>
                    <div className="w-full">
                        {widgetImages()?.desktopImg && (
                            <img
                                className="sm:block hidden max-w-full max-h-full"
                                src={widgetImages()?.desktopImg}
                                alt={`${getProductAttributes(product)} - Protection Features`}
                            />
                        )}
                        {widgetImages()?.mobileImg && (
                            <img
                                className="sm:hidden block max-w-full max-h-full"
                                src={widgetImages()?.mobileImg}
                                alt={`${getProductAttributes(product)} - Protection Features`}
                            />
                        )}
                    </div>
                </div>
                <div className="product-info lg:basis-1/4 xl:basis-[21.5%] flex flex-col justify-center">
                    <div className="mt-5 p-0 lg:mt-0">
                        <div
                            className="flex items-center justify-center text-center"
                        >
                            <p className="text-[#fa621d] text-[1.875rem] leading-8 font-bold lg:text-[1.75rem]">
                                <Money value={{ currencyCode: CURRENCY, amount: product?.currentVariant?.node?.price }} />
                            </p>
                            {product?.currentVariant?.node?.compareAtPrice && (
                                <p className="text-sm text-[#585858] line-through leading-4 font-medium ml-1">
                                    <Money value={{ currencyCode: CURRENCY, amount: product?.currentVariant?.node?.compareAtPrice }} />
                                </p>
                            )}
                            
                        </div>
                    </div>
                    <div className="w-full">
                        <p className="text-small text-[#959595] leading-5 font-bold py-0.5 uppercase text-center mt-0.5">
                            Free
                            Shipping
                        </p>
                        <form className="max-w-[25rem] w-full mx-auto py-1.5">
                            {hasOneDropdownWithOneValue && (
                                <div className="flex flex-col gap-2">
                                    {product.options.map((option) => {
                                        const selectedValue = product.currentVariant.node.selectedOptions
                                            .find((selectedOption) => selectedOption.name === option.name)?.value;

                                        if (option.name === "Color" || option.name === "COLOR") {
                                            return (
                                                <VariantSelectorCircles
                                                    colors={option.values}
                                                    selected={selectedValue}
                                                    onVariantChange={(value) => {
                                                        const variant = getVariantByOptions({ name: option.name, value });
                                                        setVariant(variant);
                                                    }}
                                                    variantColors={variantColors}
                                                    key={option.name}
                                                />
                                            );
                                        } 

                                        return (
                                            <select
                                                className="border border-gray-601 py-1.5 px-4 pr-8 text-xs text-black lg:max-w-[235px]"
                                                onChange={(event) => {
                                                    const variant = getVariantByOptions({ name: option.name, value: event.target.value });
                                                    setVariant(variant);
                                                }}
                                                key={option.name}
                                            >
                                                {option.values.map((variantOption) => (
                                                    <option
                                                        defaultValue={selectedValue}
                                                        value={variantOption}
                                                        key={variantOption}
                                                    >
                                                        {variantOption}
                                                    </option>
                                                ))}
                                            </select>
                                        );
                                    })}
                                </div>
                            )}

                            <QuantitySelector
                                quantity={quantity}
                                onQuantityChange={(value) => handleQuantityChange(value)}
                            />
                            
                            <div className="relative z-0">
                                <button 
                                    type="button"
                                    className="relative btn-primary mx-auto max-w-[11.8rem] px-4 py-1 text-small uppercase font-semibold leading-[1.875rem] lg:text-sm lg:font-bold lg:leading-[1.875rem] z-0"
                                    onClick={() => handleAddToCart(product?.currentVariant?.node?.id, quantity, checkoutCustomAttributes)}
                                >
                                    Add to cart
                                    <CartIcon fill="currentColor" className="absolute w-5 right-6" />
                                </button>
                            </div>
                        </form>
                        <p className="py-0.5 text-center">
                            <Link className="font-semibold text-small uppercase text-[#0097eb] leading-6" to={`/products/${product?.handle}`}>
                                Product Details
                                <Arrow fill="#0097eb" className="inline-block ml-1 mb-0.5 -rotate-90 w-[0.56rem] h-[0.56rem]" />
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductItem;
